/**
 * Which of an interview's actions are ticked off, by their words, kept on
 * this device. One store for the review's Actions and Home's open actions,
 * so a tick in one shows in the other.
 *
 * @module components/apps/probe/probeActionsDone
 */
import * as Schema from "effect/Schema";
import { useCallback, useSyncExternalStore } from "react";

import { getLocalStorageItem, setLocalStorageItem } from "../../../hooks/useLocalStorage";

const DoneActions = Schema.Array(Schema.String);

const doneKey = (interviewId: string) => `probe:follow-ups-done:${interviewId}`;

const cache = new Map<string, ReadonlySet<string>>();
const listeners = new Set<() => void>();
let version = 0;

const read = (interviewId: string): ReadonlySet<string> => {
  const known = cache.get(interviewId);
  if (known !== undefined) return known;
  let done: ReadonlySet<string>;
  try {
    done = new Set(getLocalStorageItem(doneKey(interviewId), DoneActions) ?? []);
  } catch {
    done = new Set();
  }
  cache.set(interviewId, done);
  return done;
};

/** Tick an action off, or back on. */
export const setActionDone = (interviewId: string, text: string, done: boolean): void => {
  const next = new Set(read(interviewId));
  if (done) next.add(text);
  else next.delete(text);
  cache.set(interviewId, next);
  try {
    setLocalStorageItem(doneKey(interviewId), [...next], DoneActions);
  } catch {
    // A full or blocked storage keeps the tick for this visit only.
  }
  version += 1;
  for (const listener of listeners) listener();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Whether an interview's action is ticked off, redrawn when any tick changes. */
export function useActionsDone(): (interviewId: string, text: string) => boolean {
  const at = useSyncExternalStore(subscribe, () => version);
  // oxlint-disable-next-line react-hooks/exhaustive-deps -- `at` is the store's version: a new tick, a new reader.
  return useCallback((interviewId: string, text: string) => read(interviewId).has(text), [at]);
}

/** Forget what was read, as a reload would. For tests. */
export const resetActionsDone = (): void => {
  cache.clear();
  version += 1;
};
